import { React } from 'react'
import { motion } from 'framer-motion'
import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import ServicesList from 'components/ServicesList'
import './ServicesPages.scss'

const ProjetsCreatifsIoT = () => {
	useDocumentTitle(`Projets Créatifs & IoT - Installations Interactives et Objets Connectés`)
	useDocumentConfig(`blue`, false)

	return (
		<motion.main
			className='service-page'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={3}
				scrollTo='Découvrir nos solutions'
			>
				Projets créatifs<br />
				& IoT
			</Hero>

			<section className='service-intro'>
				<div className='container'>
					<h1 className='align-center'>Projets Créatifs & IoT</h1>
					<p className='lead align-center'>
						Donnez vie à vos idées les plus innovantes avec des installations interactives et des solutions IoT sur-mesure
					</p>
				</div>
			</section>

			<section className='service-content'>
				<div className='container'>
					<div className='content-grid'>
						<div className='content-main'>
							<h2>L'innovation au service de l'expérience utilisateur</h2>
							<p>
								Nous explorons les frontières entre le digital et le physique pour créer des expériences 
								uniques et mémorables. Des installations interactives aux objets connectés, en passant 
								par les interfaces tangibles et les expériences immersives, nous transformons vos idées 
								créatives en réalité technique, en combinant design, technologie et innovation.
							</p>

							<h3>Domaines d'expertise créative</h3>
							<ul className='no-prefix'>
								<li><strong>Installations interactives</strong> : Murs tactiles, projections mapping, capteurs de mouvement</li>
								<li><strong>Objets connectés (IoT)</strong> : Prototypes, produits connectés, domotique sur-mesure</li>
								<li><strong>Art numérique</strong> : Œuvres génératives, visualisations de données artistiques</li>
								<li><strong>Expériences immersives</strong> : Réalité augmentée, installations multi-sensorielles</li>
								<li><strong>Interfaces physiques</strong> : Bornes interactives, dispositifs tactiles personnalisés</li>
							</ul>

							<h3>Technologies créatives maîtrisées</h3>
							<div className='tech-grid'>
								<div className='tech-category'>
									<h4>Hardware IoT</h4>
									<p>Arduino, Raspberry Pi, ESP32, capteurs divers, actionneurs</p>
								</div>
								<div className='tech-category'>
									<h4>Protocoles connectés</h4>
									<p>MQTT, WebSockets, Bluetooth, WiFi, LoRa, Zigbee</p>
								</div>
								<div className='tech-category'>
									<h4>Frameworks créatifs</h4>
									<p>Processing, P5.js, OpenFrameworks, TouchDesigner, Unity</p>
								</div>
								<div className='tech-category'>
									<h4>Interaction physique</h4>
									<p>Computer Vision, Kinect, Leap Motion, capteurs environnementaux</p>
								</div>
							</div>

							<h3>Types de projets réalisés</h3>
							<ul className='no-prefix'>
								<li>Mur interactif pour musée avec détection de présence</li>
								<li>Installation artistique réagissant aux données environnementales</li>
								<li>Borne tactile multi-touch pour événementiel</li>
								<li>Système domotique personnalisé avec interface web</li>
								<li>Sculpture cinétique contrôlée par application mobile</li>
								<li>Tableau de bord IoT pour monitoring industriel</li>
								<li>Expérience de réalité augmentée pour retail</li>
								<li>Dispositif de mesure environnementale connecté</li>
							</ul>

							<h3>Notre approche créative</h3>
							<ul className='no-prefix'>
								<li><strong>Co-création</strong> : Workshops créatifs pour définir ensemble le concept</li>
								<li><strong>Prototypage rapide</strong> : Tests et itérations pour valider les idées</li>
								<li><strong>Design thinking</strong> : Approche centrée utilisateur et expérience</li>
								<li><strong>Innovation ouverte</strong> : Veille technologique et expérimentation continue</li>
								<li><strong>Documentation détaillée</strong> : Guides techniques pour maintenance et évolution</li>
							</ul>

							<h3>SceneArtIO - Notre solution SaaS</h3>
							<p>
								Nous développons <strong>SceneArtIO</strong>, une plateforme no-code qui permet de créer 
								des applications interactives pour bornes tactiles en mode "PowerPoint". Interface intuitive, 
								glisser-déposer, modèles sectoriels - créez vos applications en quelques heures au lieu de semaines.
							</p>
							<ul className='no-prefix'>
								<li><strong>Création sans code</strong> : Interface drag & drop accessible à tous, même sans compétences techniques</li>
								<li><strong>Modèles sectoriels prêts</strong> : Écrans d'accueil, menus de navigation, galeries photos, vidéos, questionnaires interactifs adaptés à chaque secteur</li>
								<li><strong>Personnalisation simple</strong> : Ajout de votre contenu, couleurs, logos en quelques clics</li>
								<li><strong>Déploiement rapide</strong> : De l'idée à l'installation en quelques jours</li>
								<li><strong>Multi-supports</strong> : Bornes tactiles, écrans interactifs, tablettes</li>
							</ul>
							<div className='align-center' style={{ margin: '1.5rem 0' }}>
								<Button to="https://sceneartio.com/" version='1' size='smaller' target="_blank" rel="noreferrer noopener">
									Découvrir SceneArtIO
								</Button>
							</div>

							<h3>Applications sectorielles</h3>
							<ul className='no-prefix'>
								<li><strong>Culture & Musées</strong> : Parcours interactifs, médiation numérique, bornes d'information</li>
								<li><strong>Parcs de loisirs</strong> : Bornes d'accueil, plans interactifs, jeux tactiles</li>
								<li><strong>Santé</strong> : Bornes d'information patient, écrans d'attente interactifs</li>
								<li><strong>Tourisme</strong> : Offices de tourisme, points d'information, cartes interactives</li>
								<li><strong>Éducation</strong> : Écoles, universités, supports pédagogiques interactifs</li>
								<li><strong>Bibliothèques</strong> : Catalogues interactifs, animations culturelles</li>
								<li><strong>Escape rooms</strong> : Énigmes tactiles, indices interactifs</li>
								<li><strong>Laboratoires scientifiques</strong> : Présentation de recherches, vulgarisation</li>
								<li><strong>Événementiel d'entreprise</strong> : Salons, conférences, stands interactifs</li>
								<li><strong>Retail & Marketing</strong> : Vitrines connectées, expériences en magasin</li>
								<li><strong>Smart Building</strong> : Bureaux intelligents, espaces adaptatifs</li>
								<li><strong>Art & Design</strong> : Œuvres interactives, installations permanentes</li>
								<li><strong>Industrie</strong> : Monitoring IoT, maintenance prédictive</li>
							</ul>
						</div>

						<aside className='content-sidebar'>
							<div className='sidebar-box'>
								<h3>Pourquoi nous ?</h3>
								<ul className='benefits-list no-prefix'>
									<li>Créativité technique</li>
									<li>Expertise hardware/software</li>
									<li>Prototypage agile</li>
									<li>Solutions évolutives</li>
									<li>Support long terme</li>
									<li>Open source privilégié</li>
								</ul>
							</div>

							<div className='sidebar-box'>
								<h3>Budget projets créatifs</h3>
								<p className='price-info'>
									<strong>5 000€ - 25 000€ HT</strong><br />
									Installation interactive
								</p>
								<p className='small'>
									Prototype IoT : dès 2 000€<br />
									Projets complexes : sur étude
								</p>
								<br />
								<p className='price-info'>
									<strong>Consulting : 120€/h HT</strong><br />
									Accompagnement créatif
								</p>
							</div>

							<div className='sidebar-box'>
								<h3>Livrables types</h3>
								<ul className='benefits-list no-prefix'>
									<li>Prototype fonctionnel</li>
									<li>Code source commenté</li>
									<li>Schémas électroniques</li>
									<li>Documentation technique</li>
									<li>Guide d'installation</li>
									<li>Formation utilisateur</li>
								</ul>
							</div>

							<div className='sidebar-box cta-box'>
								<h3>Une idée créative ?</h3>
								<p>Explorons ensemble les possibilités techniques de votre projet</p>
								<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1' size='small'>
									Prendre rendez-vous
								</Button>
							</div>

							<div className='sidebar-box'>
								<h3>SceneArtIO</h3>
								<p className='price-info'>
									<strong>Solution SaaS no-code</strong>
								</p>
								<p className='small'>
									Créez vos applications pour bornes tactiles sans une seule ligne de code
								</p>
								<p>
									Abonnement annuel ou mensuel
								</p>
								<Button to="https://sceneartio.com/" version='1' size='smaller' target="_blank" rel="noreferrer noopener">
									Découvrir SceneArtIO
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='service-process'>
				<div className='container'>
					<h2 className='align-center'>Process de création innovant</h2>
					<div className='process-steps'>
						<div className='process-step'>
							<span className='step-number'>01</span>
							<h3>Idéation</h3>
							<p>Brainstorming créatif et exploration des possibles</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>02</span>
							<h3>Prototype</h3>
							<p>Proof of concept technique et tests utilisateurs</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>03</span>
							<h3>Réalisation</h3>
							<p>Développement complet hardware et software</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>04</span>
							<h3>Déploiement</h3>
							<p>Installation, calibrage et maintenance</p>
						</div>
					</div>
				</div>
			</section>

			<ServicesList currentService='projets-creatifs-iot' />

			<div className='column-full align-center'>
				<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1'>
					Concrétiser votre idée
				</Button>
			</div>
		</motion.main>
	)
}

export default ProjetsCreatifsIoT